import {ChangeDetectionStrategy, Component, computed, inject, output} from '@angular/core';
import {CommonModule} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ChatClientService} from 'src/app/services/AIReports/chat-client.service';
import {ChatStore} from 'src/app/services/store';
import {AiChatFacade} from '../chat/ai-chat.facade';
import {DialogBaseComponent} from 'src/app/components/common/dialog/dialog-base/dialog-base.component';
import {TranslateHandler} from 'src/app/common/helpers/translate-handler';
import {switchMap} from 'rxjs/internal/operators/switchMap';
import {filter} from 'rxjs/internal/operators/filter';
import {from} from 'rxjs';
import {MessageDialogService} from 'src/app/components/common/message-dialog/services/message-dialog.service';
import {RelativeTimePipe} from "@pipes/AIReports";

@Component({
  selector: 'app-history',
  templateUrl: './history.component.html',
  styleUrl: './history.component.scss',
  standalone: true,
  imports: [CommonModule, TranslateModule, RelativeTimePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HistoryComponent extends DialogBaseComponent {
  private readonly chatClient = inject(ChatClientService);
  private readonly chatStore = inject(ChatStore);
  private readonly facade = inject(AiChatFacade);
  private readonly translateHandler = inject(TranslateHandler);
  private readonly messageDialogService = inject(MessageDialogService);
  readonly chatThreadsResource = this.chatClient.chatThreadsResource;
  readonly activeThreadId = this.chatStore.thread_id;

  readonly hasChatThreads = computed(
    () => this.chatThreadsResource.hasValue() &&
      this.chatThreadsResource.value().length > 0
  );

  readonly threadSelected = output<void>();

  trackById(index: number, item: any) {
    return item.thread_id;
  }

  async selectThread(thread: any) {
    await this.facade.selectThread(thread);
    this.threadSelected.emit();
  }

  async deleteThread(thread_id: string, event: Event) {
    event.stopPropagation();
    const message = this.translateHandler.translate(
      'aiReport.lbl_chat_thread_delete'
    );
    this.messageDialogService.openDeleteConfirmation(message).pipe(
      filter(confirmed => confirmed),
      switchMap(() => from(this.facade.deleteThread(thread_id))),
    ).subscribe({
      next: () => this.chatThreadsResource.reload(),
      error: (err) => console.error('Failed to delete thread', err)
    });
  }

  async deleteAllThreads(event: Event) {
    event.stopPropagation();
    if (!this.hasChatThreads()) return;
    if (!confirm('Delete all chats?')) return;
    await this.facade.deleteAllThreads(
      this.chatThreadsResource.value().map(({ thread_id }: any) => thread_id)
    );
    this.chatThreadsResource.reload();
  }

  async newThread() {
    await this.facade.newThread();
  }

  async cancel() {
    await this.facade.newThread();
    this.close();
  }
}
